import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PrimaryButton } from '@/components';

interface SendInviteSectionProps {
  onSendInvite: (email: string) => void;
  onCancel?: () => void;
  isLoading?: boolean;
  defaultEmail?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SendInviteSection({
  onSendInvite,
  onCancel,
  isLoading = false,
  defaultEmail = '',
}: SendInviteSectionProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState(defaultEmail);
  const [error, setError] = useState<string | null>(null);

  const isValidEmail = EMAIL_REGEX.test(email);

  const handleSubmit = useCallback(() => {
    if (!email.trim()) {
      setError(t('employees.inviteDialog.errors.emailRequired'));
      return;
    }

    if (!isValidEmail) {
      setError(t('employees.inviteDialog.errors.emailInvalid'));
      return;
    }

    setError(null);
    onSendInvite(email);
  }, [email, isValidEmail, onSendInvite, t]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setError(null);
  };

  return (
    <div className="p-4 border border-border rounded-lg bg-surface-darker/30">
      <h2 className="text-sm font-medium text-onsurface mb-2">
        {t('contact.invite.title')}
      </h2>
      <p className="text-sm text-muted mb-3">
        {t('contact.invite.description')}
      </p>

      <div className="mb-3">
        <label
          htmlFor="invite-section-email"
          className="block mb-1 text-sm font-medium text-onsurface"
        >
          {t('employees.inviteDialog.emailLabel')}
        </label>
        <input
          id="invite-section-email"
          type="email"
          value={email}
          onChange={handleEmailChange}
          placeholder={t('employees.inviteDialog.emailPlaceholder')}
          disabled={isLoading}
          className="py-1.5 sm:py-2 px-3 border block w-full bg-field border-border rounded-sm sm:text-sm text-onfield placeholder:text-onfield/30 focus:z-10 focus:border-primary focus:ring-primary focus:outline-none disabled:opacity-50 disabled:pointer-events-none"
        />
        {error && <p className="mt-1 text-sm text-error">{error}</p>}
      </div>

      <div className="flex justify-end gap-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-muted hover:text-onsurface transition-colors disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
        )}
        <PrimaryButton
          onClick={handleSubmit}
          disabled={isLoading || !isValidEmail}
        >
          {isLoading
            ? t('employees.inviteDialog.sending')
            : t('contact.invite.sendButton')}
        </PrimaryButton>
      </div>
    </div>
  );
}
